/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef } from 'react';
import * as Gdpr from 'core/store/ducks/gdpr';
import { Asset, Ducks, MenuUiArea } from '@wit/vtv-x-foundation-lib';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PrivateRoutePath } from 'routes/paths';
import Constants from 'configs/Constants';

import * as Assets from 'core/store/ducks/assets';
import { StoreState } from 'store/store';
import * as OnDemand from 'store/ducks/onDemand';
import translate from 'services/i18n/translate';

import { SimpleHeader, Spinner } from 'ui/components';
import Overlay from 'ui/components/Hero/Overlay';
import BackgroundOverlay from 'assets/images/background/LandingPage_BackgroundImage.png';
import RouterUtils from 'services/utils/RouterUtils';
import { useHistoryWithState } from 'hooks/useHistoryWithState';

import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { RailType } from 'types/Rail';
import useIsOnViewportObserver from 'hooks/useIsOnViewportObserver';
import Grid from '../../components/Grid';
import { useRouter } from '../../../hooks/useRouter';

interface UriParams {
  menuItemId: string;
  categoryItemId?: string;
}

export default function OnDemandGrid(): JSX.Element | null {
  const history = useHistoryWithState();
  const router = useRouter();
  const uriParams = useParams<UriParams>();
  const dispatch = useDispatch();

  let menuItemId: number | undefined = Number.parseInt(uriParams.menuItemId || '', 10);
  let categoryItemId: number | undefined = Number.parseInt(uriParams.categoryItemId || '', 10);
  menuItemId = !Number.isNaN(menuItemId) ? menuItemId : undefined;
  categoryItemId = !Number.isNaN(categoryItemId) ? categoryItemId : undefined;

  const rail = useSelector((store: StoreState) => OnDemand.Selectors.getRail(store, menuItemId));
  const categoryRail = useSelector((store: StoreState) => OnDemand.Selectors.getCategoryRail(store, categoryItemId));

  const { id, channelId, title, type } = (categoryItemId ? categoryRail : rail) || {};

  const isProcessed = useSelector(OnDemand.Selectors.isProcessed);
  const processingRequestedGridPage = useSelector(OnDemand.Selectors.processingRequestedGridPage);
  const isGdprConsent = useSelector(Gdpr.Selectors.hasAgreedToConsent);

  const assets = useSelector((state: StoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(state, BADGES_PRIORITY_PER_SECTION_RAIL.ONDEMAND.RAILS, (store) =>
      channelId && type !== RailType.RECOMMENDATIONS
        ? Assets.Selectors.getAssets(store, channelId)
        : Ducks.Recommendations.Selectors.assets(store, { menuUiArea: MenuUiArea.VOD, menuItemId: id || 0 })
    )
  );
  const allAssetsLoaded = useSelector((state: StoreState) =>
    type === RailType.RECOMMENDATIONS
      ? Ducks.Recommendations.Selectors.assets(state, { menuUiArea: MenuUiArea.VOD, menuItemId: id || 0 })
      : true
  );

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    dispatch(
      OnDemand.ActionCreators.initNextLevel({
        menuItemId,
        categoryItemId,
        channelId,
      })
    );
  }, []);

  useEffect(() => {
    if (!isGdprConsent && type === RailType.RECOMMENDATIONS) {
      history.replace(PrivateRoutePath.ONDEMAND);
    }
  }, [history, isGdprConsent, type]);
  const existsMoreAssetsToLoad = (assets || []).length % Constants.ASSETS_PER_GRID_PAGE === 0 || !allAssetsLoaded;

  // #region INFINIT SCROLL
  const gridRef = useRef(null);
  const isOnViewport = useIsOnViewportObserver(gridRef, {
    root: null,
    rootMargin: '0px',
    threshold: [0.9, 1],
  });
  useEffect(() => {
    if (isOnViewport && !processingRequestedGridPage && existsMoreAssetsToLoad) {
      dispatch(
        OnDemand.ActionCreators.loadGridAssets(
          channelId || id || 0,
          type === RailType.RECOMMENDATIONS ? { menuUiArea: MenuUiArea.VOD, menuItemId: id || 0 } : undefined
        )
      );
    }
  }, [channelId, dispatch, existsMoreAssetsToLoad, id, isOnViewport, processingRequestedGridPage, type]);
  // #endregion INFINIT SCROLL

  if (!isProcessed) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  if (!id) {
    history.replace(PrivateRoutePath.ONDEMAND);
    return null;
  }

  const onCardClick = (asset: Asset) => {
    router.push({ asset, menuItemId });
  };

  const backLabel = categoryItemId ? rail?.title : translate['entrypoint.ondemand'];
  const backUrl = categoryItemId
    ? RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.ONDEMAND_NEXT_LEVEL, [rail?.id])
    : PrivateRoutePath.ONDEMAND;

  return (
    <div id="OnDemand_grid" className="ondemand-nextlevel ondemand-grid template-holder back-nav">
      <Overlay backgroundImage={BackgroundOverlay} />
      {categoryItemId ? (
        <SimpleHeader subtitle={title || ''} backLabel={backLabel} backUrl={backUrl} />
      ) : (
        <SimpleHeader subtitle={title || ''} hideBackIcon />
      )}

      <Grid railId="grid" className="no-background" assets={assets} menuItemId={menuItemId} onCardClick={onCardClick} />
      <div ref={gridRef} className="load-more-btn-container margin-top-20 margin-bottom-66">
        {processingRequestedGridPage ? (
          <div className="layout-spinner-container">
            <Spinner isActive />
          </div>
        ) : null}
      </div>
    </div>
  );
}
